import { useState } from 'react';
import { List, Typography, styled } from '@mui/material';
import { Work } from '@mui/icons-material';
import { Row } from 'components/Layout';
import { useHistory } from 'react-router-dom';
import { createLpCompanyEditRoute } from 'adminConstants/routes';
import SearchResultItem from './SearchResultItem';
import useGlobalSearch from './useGlobalSearch';

const initialResultsCount = 3;

const SeeMore = styled(Typography)({
  cursor: 'pointer',
});

const ResultsHeading = styled(Row)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.xs),
}));

const ResultsList = styled(List)({
  margin: 0,
  padding: 0,
});

type Props = {
  searchQuery: string;
  vehicleId: string;
  results: {
    companies: {
      total: number;
      results: Array<{
        id: string;
        name: string;
        holdingClass: string;
        logo: string;
      }>;
    };
  };
};

const SearchResults: React.FC<Props> = ({
  results,
  searchQuery,
  vehicleId,
}) => {
  const history = useHistory();
  const [showMoreCompanies, setShowMoreCompanies] = useState(false);
  const { latestSearched, setLatestSearched } = useGlobalSearch(vehicleId);

  const initialCompanies = results.companies.results.slice(
    0,
    initialResultsCount,
  );
  const remainingCompanies =
    results.companies.results.slice(initialResultsCount);

  const handleSeeMoreCompaniesClick = () => {
    setShowMoreCompanies(true);
  };

  const handleCompanyClick = (companyId: string) => {
    const result = results.companies.results.find(
      (result) => result.id === companyId,
    );
    const url = createLpCompanyEditRoute(companyId);

    if (result) {
      setLatestSearched([
        ...latestSearched.filter((result) => result.url !== url),
        {
          category: 'companies',
          name: result.name,
          logo: result.logo,
          url,
        },
      ]);
    }

    history.push(url);
  };

  const companies = showMoreCompanies
    ? results.companies.results
    : initialCompanies;

  return (
    <>
      <ResultsHeading spacing="xs" centered>
        <Work fontSize="small" />
        <Typography variant="h6" color="grey.600">
          Companies
        </Typography>
      </ResultsHeading>
      <ResultsList>
        {companies.map((company) => (
          <SearchResultItem
            onClick={(id) => {
              handleCompanyClick(id);
            }}
            key={`${company.id}-${company.holdingClass}`}
            id={company.id}
            name={company.name}
            logo={company.logo}
          >
            <Typography variant="body2">{company.holdingClass}</Typography>
          </SearchResultItem>
        ))}
      </ResultsList>
      {remainingCompanies.length > 0 && !showMoreCompanies && (
        <SeeMore
          variant="body2"
          color="secondary.main"
          onClick={handleSeeMoreCompaniesClick}
        >
          See {remainingCompanies.length} more
        </SeeMore>
      )}
      {!!searchQuery && companies.length === 0 && (
        <Typography variant="body2">No "{searchQuery}" in companies</Typography>
      )}
    </>
  );
};

export default SearchResults;
